import { NgRedux, NgReduxModule } from '@angular-redux/store';
import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationRef, enableProdMode, ErrorHandler, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import { BrowserModule, Title } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { DropDownsModule, SharedModule } from '@progress/kendo-angular-dropdowns';
import { LoadingModule } from 'ngx-loading';
import { ToastNoAnimation, ToastNoAnimationModule, ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { FrontendUpdateComponent } from './shared/components/frontend-update/frontend-update.component';
import { PendingWriteInterceptor } from './shared/services/pending-write.service';
import { routing } from './app.routing';
import { ApplicationUserActions } from './pb-screen/application.user.actions';
import { ApplicationUserService } from './pb-screen/services/application.user.service';
import { ChannelEventFactory } from './pb-screen/services/channel.event.factory';
import { ChannelService, SignalrWindow } from './pb-screen/services/channel.service';
import { AuthorizedUser, HomeService } from './pb-screen/services/home.service';
import { ToScreenService } from './pb-screen/services/to-screen.service';
import { HomeComponent } from './shared/components/home.component';
import { ModalManagerService } from './shared/services/modal-manager.service';
import { SharedService } from './shared/services/shared.service';
import { SmsService } from './shared/services/sms.service';
import { SharedActions } from './shared/shared.actions';
import { SharedModules } from './shared/shared.modules';
import { IAppState } from './store/IAppState';
import { store } from './store/store';
import { InitialGuard } from './shared/guards/initial.guard';
import { RedirectGuard } from './shared/guards/redirect.guard';
import { DynamicCaptionService } from './shared/services/dynamic-caption.service';
import { settingComponent } from './setting-modal/setting-modal.component';
import { settingAction } from './setting-modal/setting.action';
import { CopySettingComponent } from './copy-setting-modal/copy-setting-modal.component';


import { InputsModule } from "@progress/kendo-angular-inputs";

import { userSetupComponent } from './pb-screen/userSetup/userSetup-modal.component';
import { PlanningBoardActions } from './planningBoard/planningBoard.action';
import { PlanningBoardService } from './planningBoard/services/planningBoard.service';
// import { PlanningBoardSignalRListener } from './planningBoard/services/PlanningBoardSignalRListener';
import { PBoardChannelService } from './planningBoard/services/PBoardchannel.service';
import { DriverAgendaService } from './driverAgenda/services/driverAgenda.service';
import { MainPageComponent } from './mainPage/main-page.component';
import { AgendaActions } from './shared/agenda.actions';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { VersionSettings } from './pb-screen/static/client/versionSettings';
import { VersionComponent } from './shared/components/modals/version-modal/version-modal.component';
import { NguiAutoCompleteModule } from '@ngui/auto-complete';
import { DepotOutActions } from './depot-out/depot-out.actions';
import { DepotInActions } from './depot-in/depot-in.actions';
import { PoolActions } from './pool/pool.actions';
import { AnnouncementActions } from './announcement/announcement.actions';
import { StackerActions } from './stacker-screen/stacker.actions';
import { ShuttleListService } from './services/shuttle.print.service';

import { MSAL_INSTANCE, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalService } from './msal';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from './msal/constants';
import { MsalGuardConfiguration } from './msal/msal.guard.config';
import { MsalInterceptorConfig } from './msal/msal.interceptor.config';

import { MsalComponent } from "./MsalComponents"
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SimpleModalModule } from 'ngx-simple-modal';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { PlanningDatesService } from './planningBoard/services/planningDates.service';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { HttpErrorInterceptor } from './shared/services/error.interceptor';
import { LanguageSelectorComponent } from './shared/components/language-selector/language-selector.component';
import { ErrorsHandlerService } from './shared/services/app-logging/errors-handler.service';
import { ApplicationInsightsLogger } from './shared/services/app-logging/applications-insights-logger';
import { ColorPickerComponent } from './shared/components/color-picker/color-picker.component';
import { ButtonVisibilityService } from './shared/services/button-visibility.service';
import { UserAuthorizationModule } from './users-authorizations/user-authorization.module';
import { UsersAuthorizationService } from './users-authorizations/user-authorization.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./resources/", `.json?v=${VersionSettings.versionNumber}`);
}

export function getWindow() { return window; }


function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: `https://login.microsoftonline.com/${environment.tenant}/`,
      redirectUri: environment.redirectUrl,
      postLogoutRedirectUri: environment.redirectUrl,

      //navigateToLoginRequestUrl: true
    },
    cache: {
      storeAuthStateInCookie: false,
    }
  });
}

// Azure AD B2C tenants cannot expose an "api://<clientId>" Application ID URI - the portal forces an
// https:// identifier URI - so those customers set apiScope explicitly in their environment JSON
// (e.g. "https://poot-test-pt4.go4tms.net/user_impersonation"). Everyone else keeps the default.
export function getApiScope(): string {
  return environment.apiScope || `api://${environment.clientId}/access_as_user`;
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfig {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  const apiScope = getApiScope();
  protectedResourceMap.set(environment.tmsApiEndpointAddress, [apiScope]);
  protectedResourceMap.set(environment.ContainerPlanningApiUri, [apiScope]);
  protectedResourceMap.set(environment.tmsUserApiEndPointAddress, [apiScope]);
  protectedResourceMap.set(environment.planningToolApiEndpointAddress, [apiScope]);

  return {
    interactionType: window !== window.parent && !window.opener
      ? InteractionType.Popup
      : InteractionType.Redirect,
    protectedResourceMap,
  };
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: window !== window.parent && !window.opener
      ? InteractionType.Popup
      : InteractionType.Redirect
  };
}

enableProdMode();
@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    HttpModule,
    HttpClientModule,
    routing,
    SharedModule,
    NgReduxModule,
    LoadingModule,
    TranslateModule,
    DropDownsModule,
    InputsModule,
    ToastNoAnimationModule,
    ToastrModule.forRoot({
      toastComponent: ToastNoAnimation,
      positionClass: 'toast-top-right',
      progressBar: true,
      preventDuplicates: true
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (HttpLoaderFactory),
        deps: [HttpClient]
      },
    }),
    SharedModules,
    UserAuthorizationModule,
    NguiAutoCompleteModule,
    SimpleModalModule.forRoot({ container: document.body }),
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.ERROR,
      // serverLoggingUrl: ClientSettings.clientSettings.containerPlanningApiUri + 'logs/log/',
      serverLoggingUrl: environment.hubEndpointAddress + '/mock',
    }),
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot()

  ],
  declarations: [AppComponent,
    FrontendUpdateComponent,
    HomeComponent,
    settingComponent,
    CopySettingComponent,
    userSetupComponent,
    MainPageComponent,
    MsalComponent,
    VersionComponent,
    LanguageSelectorComponent,
    ColorPickerComponent,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: PendingWriteInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },

    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },

    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: APP_BASE_HREF, useValue: environment.app_base_href },
    ChannelEventFactory,
    ChannelService,
    HomeService,
    AuthorizedUser,
    DriverAgendaService,
    { provide: SignalrWindow, useFactory: getWindow },
    Title,
    PlanningDatesService,
    ApplicationUserService,
    ApplicationUserActions,
    settingAction,
    SharedActions,
    ModalManagerService,
    SharedService,
    SmsService,
    ToScreenService,
    RedirectGuard,
    InitialGuard,
    DynamicCaptionService,
    PlanningBoardActions,
    PlanningBoardService,
    // PlanningBoardSignalRListener ,
    PBoardChannelService,
    AgendaActions,
    DepotOutActions,
    DepotInActions,
    PoolActions,
    AnnouncementActions,
    StackerActions,
    HomeService,
    ShuttleListService,
    ApplicationInsightsLogger,
    UsersAuthorizationService,
    { provide: ErrorHandler, useClass: ErrorsHandlerService },
    ButtonVisibilityService,
    // Note: Environment configuration is now loaded in main.ts before Angular bootstraps
    // This ensures MSAL and other services have the correct configuration at initialization
  ],
  entryComponents: [VersionComponent, MsalComponent, AppComponent],
  bootstrap: [AppComponent],
  exports: []
})
export class AppModule {
  constructor(private ngRedux: NgRedux<IAppState>) {
    ngRedux.provideStore(store);

  }

  ngDoBootstrap(ref: ApplicationRef) {
    if (window !== window.parent && !window.opener) {
      console.log("Bootstrap: MSAL");
      ref.bootstrap(MsalComponent);
    }
    else {
      //this.router.resetConfig(RouterModule);
      console.log("Bootstrap: App");
      ref.bootstrap(AppComponent);
    }
  }
}
